import type { Selection } from 'd3-selection';
import { unitCache } from './lib/unit-cache.js';
import { GraphVisualizer } from './lib/graph_visualizer/GraphVisualizer.js';
import { HomePage, type HomeViewState } from './lib/home/HomePage.js';
import { authService } from './lib/auth/AuthService.js';
import { LoginDialog } from './lib/auth/LoginDialog.js';
import { renderSiteNav, NavRoute } from './lib/nav/SiteNav.js';
import { ExplorerPage } from './lib/explorer/ExplorerPage.js';
import { TagsExplorerPage } from './lib/tags/TagsExplorerPage.js';
import { editModeService } from './lib/edit-mode/EditModeService.js';

// ============================================================================
// ROUTING
// ============================================================================

type Route =
  | { type: 'home' }
  | { type: 'recipe'; id: string }
  | { type: 'ingredients' }
  | { type: 'transformations' }
  | { type: 'tools' }
  | { type: 'ingredient'; id: string }
  | { type: 'transformation'; id: string }
  | { type: 'tool'; id: string }
  | { type: 'tags' };

function getRoute(): Route {
  const path = window.location.pathname;
  const recipeMatch = path.match(/^\/recipe\/(.+)$/);
  if (recipeMatch) return { type: 'recipe', id: recipeMatch[1] };
  // Singular, English — mirrors /recipe/<id> and the API's own
  // /api/ingredient/{id} / /api/transformation/{id} / /api/tool/{id}
  // naming. The plural Catalan routes below stay the list/browse views.
  const ingredientMatch = path.match(/^\/ingredient\/(.+)$/);
  if (ingredientMatch) return { type: 'ingredient', id: ingredientMatch[1] };
  const transformationMatch = path.match(/^\/transformation\/(.+)$/);
  if (transformationMatch) return { type: 'transformation', id: transformationMatch[1] };
  const toolMatch = path.match(/^\/tool\/(.+)$/);
  if (toolMatch) return { type: 'tool', id: toolMatch[1] };
  if (path === '/ingredients') return { type: 'ingredients' };
  if (path === '/transformacions') return { type: 'transformations' };
  if (path === '/utensilis') return { type: 'tools' };
  if (path === '/tags') return { type: 'tags' };
  return { type: 'home' };
}

/** Route type -> SiteNav's active-link id. */
function navRouteFor(route: Route): NavRoute {
  if (route.type === 'ingredients' || route.type === 'ingredient') return 'ingredients';
  if (route.type === 'transformations' || route.type === 'transformation') return 'transformations';
  if (route.type === 'tools' || route.type === 'tool') return 'tools';
  if (route.type === 'tags') return 'tags';
  return 'home';
}

/** Reference to the auth button in the top bar */
let topBarAuthBtn: HTMLButtonElement | null = null;
/** Reference to the username display in the top bar */
let topBarUsername: HTMLSpanElement | null = null;
/** Currently wired visualizer, for the logout/navigation cleanup below */
let editButtonVisualizer: GraphVisualizer | null = null;
/** Unsubscribe function for auth state changes */
let unsubscribeAuth: (() => void) | null = null;
/** Container the site nav is rendered into — kept so it can be re-rendered
 *  in place (auth or edit-mode changes) without a full dispatchRoute(),
 *  which would tear down and rebuild the entire page underneath it. */
let navContainer: HTMLElement | null = null;
let unsubscribeNavAuth: (() => void) | null = null;
let unsubscribeNavEditMode: (() => void) | null = null;

/**
 * Render the common top bar present on every page.
 * Includes the Teca title and the auth button. The edit-mode toggle
 * now lives in the recipe header strip instead (always visible there,
 * next to the reload/save/discard buttons).
 */
function renderTopBar(container: HTMLElement): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'top-bar border-b border-[var(--accent-alpha-20)] px-4 py-3 flex items-center justify-between';

  // Left: Title link
  const leftGroup = document.createElement('div');
  leftGroup.className = 'flex items-center';

  const link = document.createElement('a');
  link.href = '/';
  link.className =
    'flex items-center gap-1.5 text-2xl font-bold font-mono text-[var(--accent)] hover:text-[var(--accent-dark)] transition-colors cursor-pointer';

  const logoImg = document.createElement('img');
  logoImg.src = '/static/img/coberts.png';
  logoImg.alt = '';
  logoImg.className = 'h-6 w-auto';
  // Nudge up a couple pixels — flex-centered against the text baseline
  // otherwise reads as sitting slightly low.
  logoImg.style.position = 'relative';
  logoImg.style.top = '-3px';
  link.appendChild(logoImg);

  const logoText = document.createElement('span');
  logoText.textContent = 'Teca';
  link.appendChild(logoText);

  link.addEventListener('click', (e) => {
    e.preventDefault();
    history.pushState({}, '', '/');
    dispatchRoute();
  });
  leftGroup.appendChild(link);
  bar.appendChild(leftGroup);

  // Right: Auth button + Edit button
  const rightGroup = document.createElement('div');
  rightGroup.className = 'flex items-center gap-3';

  // Username display (shown when authenticated)
  topBarUsername = document.createElement('span');
  topBarUsername.className = 'text-sm text-[#a0a0a0] font-mono hidden';
  rightGroup.appendChild(topBarUsername);

  // Auth button (Login/Logout)
  topBarAuthBtn = document.createElement('button');
  topBarAuthBtn.id = 'top-bar-auth-btn';
  topBarAuthBtn.className =
    'px-4 py-1.5 text-sm font-bold font-mono rounded-md border border-[var(--accent-alpha-40)] text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-colors';
  topBarAuthBtn.addEventListener('click', handleAuthButtonClick);
  rightGroup.appendChild(topBarAuthBtn);

  bar.appendChild(rightGroup);
  container.appendChild(bar);

  // Update auth UI based on current state
  updateAuthUI();

  // Subscribe to auth state changes
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }
  unsubscribeAuth = authService.subscribe(updateAuthUI);

  return bar;
}

/**
 * Handle auth button click (login or logout).
 */
async function handleAuthButtonClick(): Promise<void> {
  if (authService.isAuthenticated()) {
    // Logout
    authService.logout();
    // If in edit mode, exit it
    if (editButtonVisualizer?.editMode) {
      // Goes through the recipe page's own (possibly confirmation-gated)
      // exit flow, which — once actually confirmed — updates
      // editModeService itself via the onEditModeChanged callback wired
      // in renderHomePage(). Don't also flip editModeService directly
      // here: if the user cancels that confirmation, edit mode is still
      // actually on, and editModeService must not claim otherwise.
      editButtonVisualizer.editMode = false;
    } else {
      // No recipe-page visualizer active (e.g. on the Explorer) — nothing
      // else will reset the global flag, and there's no unsaved-changes
      // confirmation to worry about outside the recipe page.
      editModeService.setEditMode(false);
    }
  } else {
    // Login
    const dialog = new LoginDialog();
    await dialog.open();
  }
}

/**
 * Update the auth UI based on current authentication state.
 */
function updateAuthUI(): void {
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getUser();

  if (topBarAuthBtn) {
    topBarAuthBtn.textContent = isAuthenticated ? 'Logout' : 'Login';
  }

  if (topBarUsername) {
    if (isAuthenticated && user) {
      topBarUsername.textContent = user.username;
      topBarUsername.classList.remove('hidden');
    } else {
      topBarUsername.classList.add('hidden');
    }
  }
}

/**
 * Track the current visualizer so handleAuthButtonClick can exit edit
 * mode on logout, and destroyCurrentVisualizer can clear the reference
 * on navigation. The edit-mode toggle itself now lives in the site nav
 * (see handleGlobalEditModeToggle()), which drives this same visualizer
 * when one is active.
 */
function trackEditVisualizer(viz: GraphVisualizer): void {
  editButtonVisualizer = viz;
}

/**
 * Site nav's edit-mode toggle click handler.
 *
 * On the recipe page: delegates to GraphVisualizer's own editMode setter
 * rather than flipping editModeService directly — exiting with unsaved
 * changes goes through a confirmation dialog that the user can cancel,
 * and editModeService must reflect what actually happened, not what was
 * requested. That actual outcome comes back via the onEditModeChanged
 * callback wired in renderHomePage().
 *
 * On the Explorer (no visualizer active): there's no such confirmation
 * flow, so editModeService is the whole story — flip it directly.
 */
function handleGlobalEditModeToggle(): void {
  if (currentVisualizer) {
    currentVisualizer.editMode = !currentVisualizer.editMode;
  } else {
    editModeService.setEditMode(!editModeService.isEditMode());
  }
}

/**
 * (Re)render the site nav in place, reflecting the given route and the
 * current auth/edit-mode state. Called on navigation, and again whenever
 * auth or edit-mode state changes without a navigation (e.g. toggling
 * edit mode, or the recipe page's own edit button changing it) — a full
 * dispatchRoute() would tear down and rebuild the entire page underneath it.
 */
function renderNav(route: Route): void {
  if (!navContainer) return;
  renderSiteNav(navContainer, {
    activeRoute: navRouteFor(route),
    onNavigate: (path) => {
      history.pushState({}, '', path);
      dispatchRoute();
    },
    isAuthenticated: authService.isAuthenticated(),
    editMode: authService.isAuthenticated()
      ? { active: editModeService.isEditMode(), onToggle: handleGlobalEditModeToggle }
      : undefined,
  });
}

/**
 * Main dispatch function — reads the URL and renders the appropriate page.
 */
async function dispatchRoute(restoreState?: HomeViewState): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  // Clean up any current visualizer/nav subscriptions before changing routes
  destroyCurrentVisualizer();
  unsubscribeNavAuth?.();
  unsubscribeNavEditMode?.();
  app.innerHTML = '';

  let route = getRoute();

  // The transformations and tools explorers require being logged in to
  // view at all — not just a hidden nav link (SiteNav.ts) but a real
  // block against direct navigation (typed URL, bookmark, refresh).
  // Redirect (not just fall through) so the URL bar reflects where the
  // visitor actually landed; replaceState rather than pushState so it
  // doesn't leave a broken back-button entry pointing at the page they
  // were turned away from. A focused transformation/tool
  // (/transformation/<id>, /tool/<id>) is still part of the same gated
  // area, so both are included here too. Tags is public — only its nav
  // link is hidden when logged out (SiteNav.ts); the page itself stays
  // reachable by direct link.
  if (
    (route.type === 'transformations' || route.type === 'transformation'
      || route.type === 'tools' || route.type === 'tool')
    && !authService.isAuthenticated()
  ) {
    history.replaceState({}, '', '/');
    route = { type: 'home' };
  }

  // Narrow-screen full-height/no-page-scroll layout (see the
  // .narrow-fill-page block in input.css) only applies to HomePage
  // (home + recipe routes) — never the ingredient/transformation
  // explorer, which scrolls normally like any other page.
  document.documentElement.classList.toggle(
    'narrow-fill-page',
    route.type === 'home' || route.type === 'recipe'
  );

  renderTopBar(app);
  navContainer = document.createElement('div');
  navContainer.id = 'site-nav-container';
  app.appendChild(navContainer);
  renderNav(route);
  unsubscribeNavAuth = authService.subscribe(() => {
    // Logging out while actually on the page (not just navigating to it
    // logged out, already handled above) must also kick the visitor out.
    if (
      (route.type === 'transformations' || route.type === 'transformation'
        || route.type === 'tools' || route.type === 'tool')
      && !authService.isAuthenticated()
    ) {
      history.replaceState({}, '', '/');
      dispatchRoute();
      return;
    }
    renderNav(route);
  });
  unsubscribeNavEditMode = editModeService.subscribe(() => renderNav(route));

  if (route.type === 'recipe') {
    await renderHomePage(app, route.id, restoreState);
  } else if (route.type === 'ingredients') {
    await renderExplorerPage(app, 'ingredient');
  } else if (route.type === 'ingredient') {
    await renderExplorerPage(app, 'ingredient', route.id);
  } else if (route.type === 'transformations') {
    await renderExplorerPage(app, 'transformation');
  } else if (route.type === 'transformation') {
    await renderExplorerPage(app, 'transformation', route.id);
  } else if (route.type === 'tools') {
    await renderExplorerPage(app, 'tool');
  } else if (route.type === 'tool') {
    await renderExplorerPage(app, 'tool', route.id);
  } else if (route.type === 'tags') {
    await renderTagsExplorerPage(app);
  } else {
    await renderHomePage(app, undefined, restoreState);
  }
}

// ============================================================================
// INGREDIENT/TRANSFORMATION/TOOL EXPLORER
// ============================================================================

async function renderExplorerPage(
  container: HTMLElement,
  entityType: 'ingredient' | 'transformation' | 'tool',
  focusId?: string,
): Promise<void> {
  try {
    // ExplorerPage.render() clears and owns its own container wholesale —
    // give it a dedicated child rather than `container` itself, which
    // already has the top bar and site nav appended into it by dispatchRoute().
    const pageContainer = document.createElement('div');
    container.appendChild(pageContainer);
    const page = new ExplorerPage(pageContainer, entityType, focusId);
    currentExplorerPage = page;
    await page.render();
  } catch (error) {
    console.error('Error rendering explorer page:', error);
    showError(container, 'No s\'ha pogut carregar l\'explorador', error);
  }
}

// ============================================================================
// TAGS EXPLORER
// ============================================================================

async function renderTagsExplorerPage(container: HTMLElement): Promise<void> {
  try {
    // Same reasoning as renderExplorerPage(): give it a dedicated child
    // rather than `container` itself, which dispatchRoute() already
    // populated with the top bar and site nav.
    const pageContainer = document.createElement('div');
    container.appendChild(pageContainer);
    const page = new TagsExplorerPage(pageContainer);
    currentTagsExplorerPage = page;
    await page.render();
  } catch (error) {
    console.error('Error rendering tags explorer page:', error);
    showError(container, 'No s\'ha pogut carregar les etiquetes', error);
  }
}

// ============================================================================
// HOME PAGE
// ============================================================================

async function renderHomePage(container: HTMLElement, focusRecipeId?: string, restoreState?: HomeViewState): Promise<void> {
  try {
    // Preload unit cache in background
    unitCache.ensureLoaded().catch(() => {});
    const homePage = new HomePage(container);
    currentHomePage = homePage;
    await homePage.render(focusRecipeId, restoreState);

    // Track the visualizer for the logout/navigation cleanup below
    const viz = homePage.getVisualizer();
    if (viz) {
      trackEditVisualizer(viz);
      setCurrentVisualizer(viz);

      // Keep editModeService in sync with this recipe page's *actual*
      // edit-mode outcome (see handleGlobalEditModeToggle()'s doc comment
      // for why this is one-directional — onEditModeChanged, not the
      // other way around).
      viz.onEditModeChanged((editing) => {
        editModeService.setEditMode(editing);
      });
      // Arriving here with the global flag already on (toggled from
      // elsewhere, e.g. the Explorer) enters edit mode immediately, so
      // "edit mode" means the same thing regardless of which page you're
      // actually looking at it from.
      if (editModeService.isEditMode() && !viz.editMode) {
        viz.editMode = true;
      }
    }
  } catch (error) {
    console.error('Error rendering home page:', error);
    showError(container, 'No s\'ha pogut carregar la pàgina d\'inici', error);
  }
}


// ============================================================================
// ERROR DISPLAY
// ============================================================================

function showError(container: HTMLElement, title: string, error: unknown): void {
  const err = document.createElement('div');
  err.className = 'py-4 max-w-full';
  err.innerHTML = `
    <div class="recipe-container p-8 text-center">
      <h2 class="text-2xl font-bold text-red-400 mb-2">ERROR: ${escapeHtml(title)}</h2>
      <p class="mb-2 text-[#a0a0a0]">Comprova la connexió i torna a carregar la pàgina.</p>
      <p class="text-sm text-[var(--accent)] font-mono">&gt; ${escapeHtml(error instanceof Error ? error.message : 'Error desconegut')}</p>
      <a href="/" class="inline-block mt-4 text-[var(--accent)] hover:underline">← Torna a l'inici</a>
    </div>
  `;
  container.appendChild(err);
}

// ============================================================================
// UTILITIES
// ============================================================================

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ============================================================================
// BOOTSTRAP
// ============================================================================

// Track the current visualizer for proper cleanup on navigation
let currentVisualizer: GraphVisualizer | null = null;
/** Currently mounted explorer page, for cleanup on navigation (same idea
 *  as currentVisualizer, for the ingredients/transformacions routes). */
let currentExplorerPage: ExplorerPage | null = null;
/** Currently mounted TagsExplorerPage, for cleanup on navigation — same
 *  idea as currentExplorerPage, for the /tags route. */
let currentTagsExplorerPage: TagsExplorerPage | null = null;
/** Currently mounted HomePage, for cleanup on navigation — same idea as
 *  currentVisualizer/currentExplorerPage. HomePage.render() attaches a
 *  window resize listener and a MutationObserver of its own, outside the
 *  GraphVisualizer it owns; without destroying this too, those stayed
 *  attached forever across every past navigation to '/' or '/recipe/:id',
 *  each one's resize handler still reaching into (id-based, not scoped to
 *  its own container) DOM lookups that a *later* page happens to share —
 *  see HomePage's own _resizeHandler/_layoutObserver doc comment. */
let currentHomePage: HomePage | null = null;

/**
 * Destroy the current visualizer if one exists.
 */
function destroyCurrentVisualizer(): void {
  if (currentVisualizer) {
    if (editButtonVisualizer === currentVisualizer) {
      editButtonVisualizer = null;
    }
    currentVisualizer.destroy();
    currentVisualizer = null;
  }
  if (currentExplorerPage) {
    currentExplorerPage.destroy();
    currentExplorerPage = null;
  }
  if (currentTagsExplorerPage) {
    currentTagsExplorerPage.destroy();
    currentTagsExplorerPage = null;
  }
  if (currentHomePage) {
    currentHomePage.destroy();
    currentHomePage = null;
  }
  // Unsubscribe from auth changes
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }
}

/**
 * Set the current visualizer (for cleanup on next navigation).
 */
function setCurrentVisualizer(viz: GraphVisualizer | null): void {
  currentVisualizer = viz;
}

// Handle back/forward navigation. event.state carries whatever
// HomePage.syncHistoryState() last wrote via history.replaceState() for
// the entry being returned to (undefined for an entry nothing ever wrote
// to, e.g. one from before this feature existed, or a route that never
// calls syncHistoryState) — dispatchRoute() treats a missing/undefined
// restoreState as "nothing to restore", same as any other navigation.
window.addEventListener('popstate', (event: PopStateEvent) => {
  destroyCurrentVisualizer();
  dispatchRoute((event.state ?? undefined) as HomeViewState | undefined);
});

// Start the application when the page loads
dispatchRoute();
